import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Upload.css";

const Upload = () => {
    const [selectedFiles, setSelectedFiles] = useState({
        ammortamento: null,
        "anagrafica-conti": null,
        "piano-servizi": null,
    });

    const [loading, setLoading] = useState({
        ammortamento: false,
        "anagrafica-conti": false,
        "piano-servizi": false,
    });

    const [lastUploads, setLastUploads] = useState({
        ammortamento: null,
        "anagrafica-conti": null,
        "piano-servizi": null,
    });

    const [message, setMessage] = useState({ type: "", text: "", category: "", link: "", linkLabel: "" });

    // Banner avviso: c'è almeno un conto in upload_conti_contab_odoo senza macro_voce_analitica.
    const [hasMissingMacro, setHasMissingMacro] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const uploadTypes = [
        {
            key: "ammortamento",
            title: "Ammortamento",
            description: "Carica il file Excel con i dati di ammortamento",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
            )
        },
        {
            key: "anagrafica-conti",
            title: "Anagrafica Conti Contabili",
            description: "Carica il file Excel con l'anagrafica dei conti",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
            )
        },
        {
            key: "piano-servizi",
            title: "Piano Servizi Consuntivo",
            description: "Carica il file Excel con il piano servizi",
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
            )
        }
    ];

    const fetchLastUploads = async () => {
        try {
            const uploads = {};
            for (const type of uploadTypes) {
                const response = await axios.get(`${API_URL}/upload/last/${type.key}`);
                uploads[type.key] = response.data.lastUpload
                    ? new Date(response.data.lastUpload).toLocaleString("it-IT")
                    : null;
            }
            setLastUploads(uploads);
        } catch (error) {
            console.error("Errore nel recupero degli ultimi caricamenti:", error);
        }
    };

    useEffect(() => {
        fetchLastUploads();
    }, []);

    // Health check: se ci sono codici contabili senza macro_voce_analitica, mostro il banner di avviso.
    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${API_URL}/upload/anagrafica-conti/health`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setHasMissingMacro(Boolean(res.data?.hasMissing));
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                console.error("Errore health check anagrafica conti:", err);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, []);

    const handleFileChange = (type, file) => {
        setSelectedFiles((prev) => ({ ...prev, [type]: file }));
        setMessage({ type: "", text: "", category: "" });
    };

    const handleUpload = async (type) => {
        if (!selectedFiles[type]) {
            setMessage({
                type: "error",
                text: "Seleziona un file prima di procedere",
                category: type
            });
            return;
        }

        setLoading((prev) => ({ ...prev, [type]: true }));
        setMessage({ type: "", text: "", category: "" });

        const formData = new FormData();
        formData.append("file", selectedFiles[type]);

        try {
            const response = await axios.post(`${API_URL}/upload/${type}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setMessage({
                type: "success",
                text: response.data.message || "File caricato con successo",
                category: type
            });
            setSelectedFiles((prev) => ({ ...prev, [type]: null }));

            // Reset input file
            const fileInput = document.querySelector(`#file-input-${type}`);
            if (fileInput) fileInput.value = "";

            await fetchLastUploads();

            // L'upload anagrafica-conti DELETE+INSERT su upload_conti_contab_odoo:
            // ricontrolla lo stato del banner avviso ora che la tabella è cambiata.
            if (type === "anagrafica-conti") {
                try {
                    const token = localStorage.getItem("token");
                    const healthRes = await axios.get(`${API_URL}/upload/anagrafica-conti/health`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setHasMissingMacro(Boolean(healthRes.data?.hasMissing));
                } catch (err) {
                    console.error("Errore re-check anagrafica conti dopo upload:", err);
                }
            }
        } catch (error) {
            console.error("Errore durante l'upload:", error);
            const data = error.response?.data;
            let text = data?.error || "Errore durante il caricamento del file";
            // Se il backend segnala una mancata corrispondenza colonne, dettagliamo l'esito
            if (Array.isArray(data?.missing) && data.missing.length > 0) {
                text += ` Colonne mancanti: ${data.missing.join(", ")}.`;
            }
            if (Array.isArray(data?.unexpected) && data.unexpected.length > 0) {
                text += ` Colonne non previste: ${data.unexpected.join(", ")}.`;
            }
            setMessage({
                type: "error",
                text,
                category: type
            });
        } finally {
            setLoading((prev) => ({ ...prev, [type]: false }));
        }
    };

    const handleDownload = async (type) => {
        try {
            const response = await axios.get(`${API_URL}/upload/download/${type}`, {
                responseType: "blob",
            });

            // Verifica se la risposta è un errore JSON invece di un file
            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${type}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);

            // 404 = il file fisico non è presente su questo ambiente. Riguarda solo gli
            // export serviti dal filesystem (ammortamento, piano-servizi): l'anagrafica
            // conti è generata dal DB e non passa mai di qui.
            if (error.response?.status === 404) {
                setMessage({
                    type: "error",
                    text: "File scaricabile da ambiente di produzione al link",
                    link: "https://lf23.youbidata.com/upload",
                    linkLabel: "LF23 prod",
                    category: type
                });
                return;
            }

            setMessage({
                type: "error",
                text: error.response?.data?.error || error.message || "Errore durante il download del file",
                category: type
            });
        }
    };

    const getTypeTitle = (key) => {
        const type = uploadTypes.find(t => t.key === key);
        return type ? type.title : key;
    };

    return (
        <div className="upload-container">
            <header className="page-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>Caricamento Files</h2>
                        <p className="header-subtitle">Gestione caricamento file Excel per il sistema</p>
                    </div>
                </div>
            </header>

            <div className="content-area">
                {hasMissingMacro && (
                    <div
                        role="alert"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "12px 16px",
                            marginBottom: 16,
                            background: "#fee2e2",
                            border: "1px solid #fca5a5",
                            borderLeft: "4px solid #dc2626",
                            borderRadius: 8,
                            color: "#991b1b",
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <span>ATTENZIONE - Aggiornare conti contabili, conti non mappati o alberature non presenti</span>
                    </div>
                )}
                <div className="upload-grid">
                    {uploadTypes.map((type) => (
                        <div className="upload-card" key={type.key}>
                            <div className="upload-card-header">
                                <h3 className="upload-card-title">{type.title}</h3>
                                <p className="upload-card-description">{type.description}</p>
                            </div>

                            <div className="upload-card-body">
                                <div className="file-input-wrapper">
                                    <label htmlFor={`file-input-${type.key}`} className="file-label">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="17 8 12 3 7 8"></polyline>
                                            <line x1="12" y1="3" x2="12" y2="15"></line>
                                        </svg>
                                        <span>
                                            {selectedFiles[type.key]
                                                ? selectedFiles[type.key].name
                                                : "Seleziona file Excel"}
                                        </span>
                                    </label>
                                    <input
                                        id={`file-input-${type.key}`}
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => handleFileChange(type.key, e.target.files[0])}
                                        className="file-input"
                                    />
                                </div>

                                <div className="upload-actions">
                                    <button
                                        className="btn-upload"
                                        onClick={() => handleUpload(type.key)}
                                        disabled={loading[type.key] || !selectedFiles[type.key]}
                                    >
                                        {loading[type.key] ? (
                                            <>
                                                <div className="loading-spinner"></div>
                                                Caricamento...
                                            </>
                                        ) : (
                                            <>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                    <polyline points="17 8 12 3 7 8"></polyline>
                                                    <line x1="12" y1="3" x2="12" y2="15"></line>
                                                </svg>
                                                Carica File
                                            </>
                                        )}
                                    </button>

                                    <button
                                        className="btn-download"
                                        onClick={() => handleDownload(type.key)}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        Scarica
                                    </button>
                                </div>

                                {message.category === type.key && (
                                    <div className={`upload-message ${message.type}`}>
                                        {message.type === "success" ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                                <polyline points="22,4 12,14.01 9,11.01"/>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="15" y1="9" x2="9" y2="15"/>
                                                <line x1="9" y1="9" x2="15" y2="15"/>
                                            </svg>
                                        )}
                                        <span>
                                            {message.text}
                                            {message.link && (
                                                <> <a href={message.link} target="_blank" rel="noopener noreferrer">{message.linkLabel}</a></>
                                            )}
                                        </span>
                                    </div>
                                )}

                                {lastUploads[type.key] && (
                                    <div className="upload-info">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        <span>Ultimo caricamento: {lastUploads[type.key]}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Upload;
